import React, { useState, useEffect } from "react";
import { getCatalogoServiciosAtencion, getNombresDiagnosticos  } from "../../../../services/historiaClinica";
import {
  PREGUNTAS_NOM24_GENERALES,
  PREGUNTAS_NOM24_GERONTOLOGIA,
  PREGUNTAS_NOM24_SALUD_MATERNA,
  PREGUNTAS_NOM24_PEDIATRIA,
} from "../../../../services/cuestionariocatalog";


const formatDateTime = (fecha) => {
  if (!fecha) return "—";
  try {
    return new Date(fecha).toLocaleString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      //hour: "2-digit",
      //minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const getResumenHeader = (consulta) => {
  return `ID de la consulta: ${String(consulta.id).slice(-5).toUpperCase()}`;
};

const BloqueConsulta = ({ titulo, resumen, children }) => {
  const [abierto, setAbierto] = useState(false);

  return (
    <div className="bloque-consulta">
      <div
        className="bloque-consulta-header"
        onClick={() => setAbierto(!abierto)}
      >
        <div className="bloque-consulta-titulo">{titulo}</div>
        <div className="bloque-consulta-resumen">{resumen}</div>
      </div>

      {abierto && <div className="bloque-consulta-contenido">{children}</div>}
    </div>
  );
};

const CampoTexto = ({ label, value, emptyText }) => (
  <div className="consulta-campo">
    <div className="consulta-label">{label}</div>
    <div className="consulta-valor">
      {value && String(value).trim() !== "" ? value : emptyText}
    </div>
  </div>
);

// Legacy mirror: "si existe se muestra, si no existe no se muestra"
const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const showBool = (v) => v !== null && v !== undefined;
const BoolText = (v) => (v ? "Confirmado" : "No confirmado");
// Preguntas sí/no del cuestionario por especialidad.
// Si la pregunta NO está agregada en la configuración, se borra su valor
// (solo para la pantalla) para que no salga "No confirmado" ni el título.
const BOOLEANOS_CUESTIONARIO = [
  "movimientos_fetales",
  "es_longitud",
  "caidas_ultimos_12_meses",
  "dependencia_aseo_personal",
  "esquema_vacunacion_geriatria",
];

const limpiarBooleanosInactivos = (registro, tipo) => {
  const seleccion =
    registro?.config_cuestionario?.custom?.[tipo]?.selected_keys;
  const copia = { ...registro };

  BOOLEANOS_CUESTIONARIO.forEach((key) => {
    // Solo aplica a respuestas sí/no (true/false); los textos se respetan
    if (typeof copia[key] !== "boolean") return;

    const prendida = Array.isArray(seleccion)
      ? seleccion.includes(key)
      : copia[key] === true; // sin configuración guardada: solo si es "Sí"

    if (!prendida) copia[key] = undefined;
  });

  return copia;
};
// ===== NOM-024: un campo se muestra solo si tiene respuesta (no vacío, no -1) =====
const tieneRespuestaNOM = (v) =>
  v !== undefined &&
  v !== null &&
  String(v).trim() !== "" &&
  String(v).trim() !== "-1";

const SI_NO = { 0: "No", 1: "Sí" };
const PRIMERA_SUBSECUENTE = { 0: "Primera vez", 1: "Subsecuente" };
const INTERVENCIONES_SMYA = {
  1: "Intervención psicosocial",
  2: "Tratamiento farmacológico",
  3: "Ambos",
};

// Todas las preguntas del cuestionario NOM en una sola lista
const PREGUNTAS_NOM = [
  ...PREGUNTAS_NOM24_GENERALES,
  ...PREGUNTAS_NOM24_GERONTOLOGIA,
  ...PREGUNTAS_NOM24_SALUD_MATERNA,
  ...PREGUNTAS_NOM24_PEDIATRIA,
];

// Convierte el número guardado en texto ("1" -> "Sí").
// Si viene "1&3" (riesgos), traduce cada uno y los separa con coma.
const textoNOM = (valor, etiquetas) => {
  if (!etiquetas) return String(valor);
  return String(valor)
    .split("&")
    .map((v) => etiquetas[v.trim()] ?? v.trim())
    .join(", ");
};

// Renglón igual a los demás, pero solo aparece si tiene respuesta
const CampoNOM = ({ label, value, etiquetas }) => {
  if (!tieneRespuestaNOM(value)) return null;
  return (
    <div className="consulta-campo">
      <div className="consulta-label">{label}</div>
      <div className="consulta-valor">{textoNOM(value, etiquetas)}</div>
    </div>
  );
};

// Quita los medicamentos vacíos (el formulario siempre arranca con un renglón en blanco)
const medicamentosConDatos = (lista) =>
  Array.isArray(lista)
    ? lista.filter(
        (m) =>
          String(m?.nombre || "").trim() !== "" ||
          String(m?.indicaciones || "").trim() !== ""
      )
    : [];


const ConsultasPaciente = ({ historiaClinica }) => {
    // Catálogo de servicios de atención (para mostrar el nombre, no el número)
  const [serviciosAtencion, setServiciosAtencion] = useState({});

  useEffect(() => {
    let vivo = true;

    getCatalogoServiciosAtencion()
      .then((lista) => {
        if (!vivo) return;
        const mapa = {};
        (lista || []).forEach((s) => {
          mapa[String(s.CATALOG_KEY)] = s.SERVICIOS_ATENCION;
        });
        setServiciosAtencion(mapa);
      })
      .catch((e) => console.error("Error cargando servicios de atención:", e));

    return () => {
      vivo = false;
    };
  }, []);
    // Nombres de los diagnósticos CIE (para mostrar "NOMBRE - CLAVE")
  const [nombresCIE, setNombresCIE] = useState({});

  useEffect(() => {
    if (!historiaClinica) return;

    // Junta las claves CIE de la consulta inicial y de todos los seguimientos
    const registros = [
      historiaClinica,
      ...(Array.isArray(historiaClinica.consultas) ? historiaClinica.consultas : []),
    ];

    const claves = registros.flatMap((r) => [
      r?.codigoCIEDiagnostico1,
      r?.codigoCIEDiagnostico2,
      r?.codigoCIEDiagnostico3,
    ]);

    let vivo = true;
    getNombresDiagnosticos(claves).then((mapa) => {
      if (vivo) setNombresCIE(mapa);
    });

    return () => {
      vivo = false;
    };
  }, [historiaClinica]);

  // "RINOFARINGITIS AGUDA - J00X"; si no hay nombre, solo la clave
  const textoCIE = (clave) => {
    if (!clave) return clave;
    const nombre = nombresCIE[String(clave).trim().toUpperCase()];
    return nombre ? `${nombre} - ${clave}` : clave;
  };

  if (!historiaClinica) {
    return <div className="consulta-empty">Sin consultas previas</div>;
  }

  // Consulta inicial
  const consultaInicial = {
   ...historiaClinica,
   tipo: "inicial",
   fecha:
     historiaClinica.fechaConsulta ||
     historiaClinica.fecha_creacion ||
     historiaClinica.creado_en,
   laboratorios: historiaClinica.laboratorios || [],
   recetas: historiaClinica.recetas || [],
 };

  const seguimientos = Array.isArray(historiaClinica.consultas)
   ? historiaClinica.consultas.map((c) => ({
       ...c,
       tipo: "seguimiento",
       fecha: c.fechaConsulta || c.fecha_consulta || c.creado_en,
     }))
   : [];

  // Timeline: más reciente → más antigua
  const consultas = [...seguimientos, consultaInicial].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );

  return (
    <div className="consultas-timeline">
      {consultas.map((consulta, index) => {
        const numeroConsulta = consultas.length - index;
        const titulo = `▾ Consulta ${numeroConsulta} · ${formatDateTime(
          consulta.fecha
        )}`;

        return (
          <BloqueConsulta
            key={index}
            titulo={titulo}
            resumen={getResumenHeader(consulta)}
          >
            <CampoNOM
              label="Servicio de atención"
              value={consulta.servicioAtencion}
              etiquetas={serviciosAtencion}
            />
            <CampoNOM
              label="Primera vez en UNEME"
              value={consulta.primeraVezUneme}
              etiquetas={SI_NO}
            />
            <CampoNOM
              label="Tipo de interrogatorio"
              value={consulta.tipoInterrogatorio}
            />
          
          {consulta.tipo !== "inicial" && (
            <>
              <CampoTexto
                label="Motivo de la consulta"
                value={consulta.motivo}
                emptyText="No registrado"
              />

              <CampoTexto
                label="Evolución del paciente"
                value={consulta.evolucion}
                emptyText="No registrada"
              />
            </>
          )}
           <CampoNOM
              label="Sintomático respiratorio sospechoso de TB"
              value={consulta.sintomaticoRespiratorioTb}
              etiquetas={SI_NO}
            />

            <CampoTexto
              label="Exploración física"
              value={consulta.exploracion_otros}
              emptyText="No registrada"
            />
                        <CampoNOM
              label="Relación temporal del diagnóstico principal"
              value={consulta.relacionTemporal}
              etiquetas={PRIMERA_SUBSECUENTE}
            />

            {tieneRespuestaNOM(consulta.codigoCIEDiagnostico1) && (
              <>
              <CampoNOM label="Diagnóstico CIE 1" value={textoCIE(consulta.codigoCIEDiagnostico1)} />
                <CampoNOM
                  label="Confirmación diagnóstica 1"
                  value={consulta.confirmacionDiagnostica1}
                  etiquetas={SI_NO}
                />
              </>
            )}

            {[2, 3].map((num) =>
              tieneRespuestaNOM(consulta[`codigoCIEDiagnostico${num}`]) ? (
                <React.Fragment key={`cie-${num}`}>
                  <CampoNOM
                    label={`¿Primera vez del diagnóstico ${num}?`}
                    value={consulta[`primeraVezDiagnostico${num}`]}
                    etiquetas={SI_NO}
                  />
                 <CampoNOM
                    label={`Diagnóstico CIE ${num}`}
                    value={textoCIE(consulta[`codigoCIEDiagnostico${num}`])}
                  />
                  <CampoNOM
                    label={`Confirmación diagnóstica ${num}`}
                    value={consulta[`confirmacionDiagnostica${num}`]}
                    etiquetas={SI_NO}
                  />
                </React.Fragment>
              ) : null
            )}

            <CampoTexto
              label="Diagnósticos"
              value={consulta.diagnostico}
              emptyText="No registrado"
            />

            <CampoTexto
              label="Pronóstico"
              value={consulta.pronostico}
              emptyText="No registrado"
            />

            <CampoNOM
              label="Intervenciones de salud mental y adicciones"
              value={consulta.intervencionesSMyA}
              etiquetas={INTERVENCIONES_SMYA}
            />

            {/* Medicamentos */}
            {medicamentosConDatos(consulta.medicamentos).length > 0 && (
              <div className="consulta-campo">
                <div className="consulta-label">Tratamiento</div>
                <div className="consulta-valor">
                  <ol style={{ margin: 0, paddingLeft: 14 }}>
                    {medicamentosConDatos(consulta.medicamentos).map((m, idx) => {
                      const nombre = (m?.nombre || "").trim();
                      const indicaciones = (m?.indicaciones || "").trim();
                      return (
                        <li key={idx}>
                          {nombre}
                          {indicaciones ? ` — ${indicaciones}` : ""}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </div>
            )}

            {/* Plan textual */}
            <CampoTexto
              label="Plan"
              value={consulta.tratamiento}
              emptyText="No registrado"
            />


            <CampoTexto
              label="Indicaciones adicionales"
              value={consulta.indicacionesAdicionales}
              emptyText="No registradas"
            />

            {PREGUNTAS_NOM.map((q) => (
              <CampoNOM
                key={q.key}
                label={q.label}
                value={consulta[q.key]}
                etiquetas={q.labels}
              />
            ))}

             {/* =========================================================
               CONSULTA 1 (INICIAL): MISMO COMPORTAMIENTO QUE LEGACY
               - NO placeholders
               - NO filtros por config
               - Solo: si existe -> se muestra
               ========================================================= */}
            {consulta.tipo === "inicial" && (() => {
              const h = limpiarBooleanosInactivos(consulta, "historia");

              const showGine =
                hasValue(h.edad_menarca) ||
                hasValue(h.ciclo_menstrual) ||
                hasValue(h.vida_sexual) ||
                hasValue(h.fecha_ultimo_papanicolaou) ||
                hasValue(h.fecha_ultima_mastografia) ||
                hasValue(h.antecedentes_obstetricos) ||
                hasValue(h.antecedentes_reproductivos) ||
                hasValue(h.fecha_ultima_menstruacion) ||
                hasValue(h.factores_riesgo_embarazo) ||
                hasValue(h.exploracion_gineco_obstetrica) ||
                hasValue(h.fondo_uterino_cm) ||
                showBool(h.movimientos_fetales) ||
                hasValue(h.frecuencia_cardiaca_fetal) ||
                hasValue(h.otros_ginecologia);

              const showPedia =
                hasValue(h.persona_responsable_paciente) ||
                hasValue(h.esquema_vacunacion) ||
                hasValue(h.tipo_parto) ||
                hasValue(h.peso_nacer) ||
                hasValue(h.talla_nacer) ||
                hasValue(h.perimetro_cefalico_nacer) ||
                hasValue(h.antecedentes_perinatales) ||
                hasValue(h.hitos_desarrollo) ||
                hasValue(h.perimetro_cefalico) ||
                showBool(h.es_longitud) ||
                hasValue(h.longitud) ||
                hasValue(h.peso_percentil) ||
                hasValue(h.talla_percentil) ||
                hasValue(h.perimetro_cefalico_percentil) ||
                hasValue(h.peso_talla_percentil) ||
                hasValue(h.otros_pediatria);

              const showGeria =
                hasValue(h.enfermedades_cronicas) ||
                hasValue(h.apoyo_familiar) ||
                showBool(h.caidas_ultimos_12_meses) ||
                hasValue(h.detalle_caidas_12_meses) ||
                showBool(h.dependencia_aseo_personal) ||
                showBool(h.esquema_vacunacion_geriatria) ||
                hasValue(h.dispositivos_movilidad) ||
                hasValue(h.otros_geriatria);

              if (!showGine && !showPedia && !showGeria) return null;

              return (
                <>
                  {showGine && (
                    <>
                      <div className="consulta-campo">
                        <div className="consulta-label"><strong>Ginecología / Obstetricia</strong></div>
                      </div>

                      {hasValue(h.edad_menarca) && (
                        <div className="consulta-campo"><div className="consulta-label">Edad menarca</div><div className="consulta-valor">{h.edad_menarca}</div></div>
                      )}
                      {hasValue(h.ciclo_menstrual) && (
                        <div className="consulta-campo"><div className="consulta-label">Ciclo menstrual</div><div className="consulta-valor">{h.ciclo_menstrual}</div></div>
                      )}
                      {hasValue(h.vida_sexual) && (
                        <div className="consulta-campo"><div className="consulta-label">Vida sexual</div><div className="consulta-valor">{h.vida_sexual}</div></div>
                      )}
                      {hasValue(h.fecha_ultimo_papanicolaou) && (
                        <div className="consulta-campo"><div className="consulta-label">Último papanicolaou</div><div className="consulta-valor">{h.fecha_ultimo_papanicolaou}</div></div>
                      )}
                      {hasValue(h.fecha_ultima_mastografia) && (
                        <div className="consulta-campo"><div className="consulta-label">Última mastografía</div><div className="consulta-valor">{h.fecha_ultima_mastografia}</div></div>
                      )}
                      {hasValue(h.antecedentes_obstetricos) && (
                        <div className="consulta-campo"><div className="consulta-label">Antecedentes obstétricos</div><div className="consulta-valor">{h.antecedentes_obstetricos}</div></div>
                      )}
                      {hasValue(h.antecedentes_reproductivos) && (
                        <div className="consulta-campo"><div className="consulta-label">Antecedentes reproductivos</div><div className="consulta-valor">{h.antecedentes_reproductivos}</div></div>
                      )}
                      {hasValue(h.fecha_ultima_menstruacion) && (
                        <div className="consulta-campo"><div className="consulta-label">Fecha última menstruación</div><div className="consulta-valor">{h.fecha_ultima_menstruacion}</div></div>
                      )}
                      {hasValue(h.factores_riesgo_embarazo) && (
                        <div className="consulta-campo"><div className="consulta-label">Factores de riesgo en embarazo</div><div className="consulta-valor">{h.factores_riesgo_embarazo}</div></div>
                      )}
                      {hasValue(h.exploracion_gineco_obstetrica) && (
                        <div className="consulta-campo"><div className="consulta-label">Exploración gineco-obstétrica</div><div className="consulta-valor">{h.exploracion_gineco_obstetrica}</div></div>
                      )}
                      {hasValue(h.fondo_uterino_cm) && (
                        <div className="consulta-campo"><div className="consulta-label">Fondo uterino (cm)</div><div className="consulta-valor">{h.fondo_uterino_cm}</div></div>
                      )}
                      {showBool(h.movimientos_fetales) && (
                        <div className="consulta-campo"><div className="consulta-label">Movimientos fetales</div><div className="consulta-valor">{BoolText(h.movimientos_fetales)}</div></div>
                      )}
                      {hasValue(h.frecuencia_cardiaca_fetal) && (
                        <div className="consulta-campo"><div className="consulta-label">FC fetal</div><div className="consulta-valor">{h.frecuencia_cardiaca_fetal}</div></div>
                      )}
                      {hasValue(h.otros_ginecologia) && (
                        <div className="consulta-campo"><div className="consulta-label">Otros (ginecología)</div><div className="consulta-valor">{h.otros_ginecologia}</div></div>
                      )}
                    </>
                  )}

                  {showPedia && (
                    <>
                      <div className="consulta-campo">
                        <div className="consulta-label"><strong>Pediatría</strong></div>
                      </div>

                      {hasValue(h.persona_responsable_paciente) && (
                        <div className="consulta-campo"><div className="consulta-label">Responsable</div><div className="consulta-valor">{h.persona_responsable_paciente}</div></div>
                      )}
                      {hasValue(h.esquema_vacunacion) && (
                        <div className="consulta-campo"><div className="consulta-label">Esquema de vacunación</div><div className="consulta-valor">{h.esquema_vacunacion}</div></div>
                      )}
                      {hasValue(h.peso_nacer) && (
                        <div className="consulta-campo"><div className="consulta-label">Peso al nacer</div><div className="consulta-valor">{h.peso_nacer}</div></div>
                      )}
                      {hasValue(h.talla_nacer) && (
                        <div className="consulta-campo"><div className="consulta-label">Talla al nacer</div><div className="consulta-valor">{h.talla_nacer}</div></div>
                      )}
                      {hasValue(h.perimetro_cefalico_nacer) && (
                        <div className="consulta-campo"><div className="consulta-label">PC al nacer</div><div className="consulta-valor">{h.perimetro_cefalico_nacer}</div></div>
                      )}
                      {hasValue(h.tipo_parto) && (
                        <div className="consulta-campo"><div className="consulta-label">Tipo de parto</div><div className="consulta-valor">{h.tipo_parto}</div></div>
                      )}
                      {hasValue(h.antecedentes_perinatales) && (
                        <div className="consulta-campo"><div className="consulta-label">Antecedentes perinatatales</div><div className="consulta-valor">{h.antecedentes_perinatales}</div></div>
                      )}
                      {hasValue(h.hitos_desarrollo) && (
                        <div className="consulta-campo"><div className="consulta-label">Hitos del desarrollo</div><div className="consulta-valor">{h.hitos_desarrollo}</div></div>
                      )}
                      {hasValue(h.perimetro_cefalico) && (
                        <div className="consulta-campo"><div className="consulta-label">Perímetro cefálico</div><div className="consulta-valor">{h.perimetro_cefalico}</div></div>
                      )}
                      {showBool(h.es_longitud) && (
                        <div className="consulta-campo"><div className="consulta-label">Es longitud</div><div className="consulta-valor">{BoolText(h.es_longitud)}</div></div>
                      )}
                      {hasValue(h.longitud) && (
                        <div className="consulta-campo"><div className="consulta-label">Longitud</div><div className="consulta-valor">{h.longitud}</div></div>
                      )}
                      {hasValue(h.otros_pediatria) && (
                        <div className="consulta-campo"><div className="consulta-label">Otros (pediatría)</div><div className="consulta-valor">{h.otros_pediatria}</div></div>
                      )}
                    </>
                  )}

                  {showGeria && (
                    <>
                      <div className="consulta-campo">
                        <div className="consulta-label"><strong>Geriatría</strong></div>
                      </div>

                      {hasValue(h.enfermedades_cronicas) && (
                        <div className="consulta-campo"><div className="consulta-label">Enfermedades crónicas</div><div className="consulta-valor">{h.enfermedades_cronicas}</div></div>
                      )}
                      {hasValue(h.apoyo_familiar) && (
                        <div className="consulta-campo"><div className="consulta-label">Apoyo familiar</div><div className="consulta-valor">{h.apoyo_familiar}</div></div>
                      )}
                      {showBool(h.caidas_ultimos_12_meses) && (
                        <div className="consulta-campo"><div className="consulta-label">Caídas últimos 12 meses</div><div className="consulta-valor">{BoolText(h.caidas_ultimos_12_meses)}</div></div>
                      )}
                      {hasValue(h.detalle_caidas_12_meses) && (
                        <div className="consulta-campo"><div className="consulta-label">Detalle de caídas</div><div className="consulta-valor">{h.detalle_caidas_12_meses}</div></div>
                      )}
                      {showBool(h.dependencia_aseo_personal) && (
                        <div className="consulta-campo"><div className="consulta-label">Dependencia (aseo personal)</div><div className="consulta-valor">{BoolText(h.dependencia_aseo_personal)}</div></div>
                      )}
                      {showBool(h.esquema_vacunacion_geriatria) && (
                        <div className="consulta-campo"><div className="consulta-label">Vacunación (geriatría)</div><div className="consulta-valor">{BoolText(h.esquema_vacunacion_geriatria)}</div></div>
                      )}
                      {hasValue(h.dispositivos_movilidad) && (
                        <div className="consulta-campo"><div className="consulta-label">Dispositivos de movilidad</div><div className="consulta-valor">{h.dispositivos_movilidad}</div></div>
                      )}
                      {hasValue(h.otros_geriatria) && (
                        <div className="consulta-campo"><div className="consulta-label">Otros (geriatría)</div><div className="consulta-valor">{h.otros_geriatria}</div></div>
                      )}
                    </>
                  )}
                </>
              );
            })()}


            {consulta.tipo !== "inicial" && (() => {
              const c = limpiarBooleanosInactivos(consulta, "consulta");
              const show =
                c.exploracion_gineco_obstetrica ||
                c.fondo_uterino_cm ||
                ((c.movimientos_fetales !== null) && (c.movimientos_fetales !== undefined)) ||
                c.frecuencia_cardiaca_fetal ||
                c.otros_ginecologia;

              if (!show) return null;

              return (
                <>
                  {/* Header (solo label, sin valor) */}
                  <div className="consulta-campo">
                    <div className="consulta-label"><strong>Ginecología / Obstetricia</strong></div>
                  </div>

                  {/* Campos (cada uno como consulta-campo label+valor) */}
                  {c.exploracion_gineco_obstetrica && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Exploración gineco-obstétrica</div>
                      <div className="consulta-valor">{c.exploracion_gineco_obstetrica}</div>
                    </div>
                  )}

                  {c.fondo_uterino_cm && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Fondo uterino (cm)</div>
                      <div className="consulta-valor">{c.fondo_uterino_cm}</div>
                    </div>
                  )}

                  {(c.movimientos_fetales !== null && c.movimientos_fetales !== undefined) && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Movimientos fetales</div>
                      <div className="consulta-valor">
                        {typeof c.movimientos_fetales === "boolean"
                          ? (c.movimientos_fetales ? "Confirmado" : "No confirmado")
                          : c.movimientos_fetales}
                      </div>
                    </div>
                  )}

                  {c.frecuencia_cardiaca_fetal && (
                    <div className="consulta-campo">
                      <div className="consulta-label">FCF (lpm)</div>
                      <div className="consulta-valor">{c.frecuencia_cardiaca_fetal}</div>
                    </div>
                  )}

                  {c.otros_ginecologia && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Otros (Ginecología)</div>
                      <div className="consulta-valor">{c.otros_ginecologia}</div>
                    </div>
                  )}
                </>
              );
            })()}

            {consulta.tipo !== "inicial" && (() => {
              const c = limpiarBooleanosInactivos(consulta, "consulta");
              const show =
                c.hitos_desarrollo ||
                c.perimetro_cefalico ||
                ((c.es_longitud !== null) && (c.es_longitud !== undefined)) ||
                c.longitud ||
                c.peso_percentil ||
                c.talla_percentil ||
                c.perimetro_cefalico_percentil ||
                c.peso_talla_percentil ||
                c.otros_pediatria;

              if (!show) return null;

              return (
                <>
                  {/* Header (solo label, sin valor) */}
                  <div className="consulta-campo">
                    <div className="consulta-label"><strong>Pediatría</strong></div>
                  </div>

                  {c.hitos_desarrollo && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Hitos del desarrollo</div>
                      <div className="consulta-valor">{c.hitos_desarrollo}</div>
                    </div>
                  )}

                  {c.perimetro_cefalico && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Perímetro cefálico (cm)</div>
                      <div className="consulta-valor">{c.perimetro_cefalico}</div>
                    </div>
                  )}

                  {(c.es_longitud !== null && c.es_longitud !== undefined) && (
                    <div className="consulta-campo">
                      <div className="consulta-label">¿Usar longitud (no talla)?</div>
                      <div className="consulta-valor">{c.es_longitud ? "Confirmado" : "No confirmado"}</div>
                    </div>
                  )}

                  {c.longitud && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Longitud (cm)</div>
                      <div className="consulta-valor">{c.longitud}</div>
                    </div>
                  )}

                  {c.peso_percentil && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Peso (percentil)</div>
                      <div className="consulta-valor">{c.peso_percentil}</div>
                    </div>
                  )}

                  {c.talla_percentil && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Talla (percentil)</div>
                      <div className="consulta-valor">{c.talla_percentil}</div>
                    </div>
                  )}

                  {c.perimetro_cefalico_percentil && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Perímetro cefálico (percentil)</div>
                      <div className="consulta-valor">{c.perimetro_cefalico_percentil}</div>
                    </div>
                  )}

                  {c.peso_talla_percentil && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Peso/Talla (percentil)</div>
                      <div className="consulta-valor">{c.peso_talla_percentil}</div>
                    </div>
                  )}

                  {c.otros_pediatria && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Otros (Pediatría)</div>
                      <div className="consulta-valor">{c.otros_pediatria}</div>
                    </div>
                  )}
                </>
              );
            })()}


            {consulta.tipo !== "inicial" && (() => {
              const c = limpiarBooleanosInactivos(consulta, "consulta");
              const show =
                ((c.caidas_ultimos_12_meses !== null) && (c.caidas_ultimos_12_meses !== undefined)) ||
                c.detalle_caidas_12_meses ||
                ((c.dependencia_aseo_personal !== null) && (c.dependencia_aseo_personal !== undefined)) ||
                c.otros_geriatria;

              if (!show) return null;

              return (
                <>
                  {/* Header (solo label, sin valor) */}
                  <div className="consulta-campo">
                    <div className="consulta-label"><strong>Geriatría</strong></div>
                  </div>

                  {(c.caidas_ultimos_12_meses !== null && c.caidas_ultimos_12_meses !== undefined) && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Caída desde última consulta</div>
                      <div className="consulta-valor">{c.caidas_ultimos_12_meses ? "Confirmado" : "No confirmado"}</div>
                    </div>
                  )}

                  {c.detalle_caidas_12_meses && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Detalle de caídas desde última consulta</div>
                      <div className="consulta-valor">{c.detalle_caidas_12_meses}</div>
                    </div>
                  )}

                  {(c.dependencia_aseo_personal !== null && c.dependencia_aseo_personal !== undefined) && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Dependencia en aseo personal</div>
                      <div className="consulta-valor">{c.dependencia_aseo_personal ? "Confirmado" : "No confirmado"}</div>
                    </div>
                  )}

                  {c.otros_geriatria && (
                    <div className="consulta-campo">
                      <div className="consulta-label">Otros (Geriatría)</div>
                      <div className="consulta-valor">{c.otros_geriatria}</div>
                    </div>
                  )}
                </>
              );
            })()}


            
          </BloqueConsulta>
        );
      })}
    </div>
  );
};

export default ConsultasPaciente;
